import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * What Prints Where — the single visible routing table for all printing.
 *
 * Four slots, four dropdowns. A print picks the pack in its slot:
 *   retail + buyer in dealer's state  -> In-State Retail slot
 *   retail + buyer out of state       -> Out-of-State Retail slot
 *   wholesale                         -> Wholesale slot
 *   titles printing                   -> Titles slot (pre-selected pack; still
 *                                        changeable on the print screen)
 * An empty slot falls back to the older behavior (active pack / first titles pack).
 */

export const ROUTE_SLOTS = [
  { key: 'pack_route_retail_in_state', label: 'In-State Retail prints' },
  { key: 'pack_route_retail_out_of_state', label: 'Out-of-State Retail prints' },
  { key: 'pack_route_wholesale', label: 'Wholesale prints' },
  { key: 'pack_route_titles', label: 'Titles prints', titles: true },
];

const PackRoutingPanel = ({ onRoutesChange, packs: packsProp = null }) => {
  const [packs, setPacks] = useState(packsProp || []);
  const [routes, setRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const publish = useCallback((r) => { onRoutesChange && onRoutesChange(r); }, [onRoutesChange]);

  useEffect(() => {
    const load = async () => {
      try {
        const [settings, packData] = await Promise.all([
          api.getSettings(),
          packsProp ? Promise.resolve(null) : api.getFormPacks(),
        ]);
        if (packData) {
          setPacks(packData || []); // all types — each slot filters to its own world
        }
        const r = {};
        ROUTE_SLOTS.forEach(s => { r[s.key] = settings?.[s.key] || ''; });
        setRoutes(r);
        publish(r);
      } catch (err) {
        console.error('Pack routing load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  const handleChange = async (key, value) => {
    const prev = routes[key];
    const next = { ...routes, [key]: value };
    setRoutes(next);
    publish(next);
    setSavingKey(key);
    try {
      await api.updateSettings({ [key]: value });
    } catch (err) {
      alert('Failed to save: ' + err.message);
      const reverted = { ...next, [key]: prev };
      setRoutes(reverted);
      publish(reverted);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-indigo-800 uppercase tracking-wide">What Prints Where</span>
        {savingKey && <span className="text-[10px] text-indigo-500 animate-pulse">Saving…</span>}
      </div>
      {loading ? (
        <p className="text-xs text-indigo-400">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {ROUTE_SLOTS.map(slot => {
            const slotPacks = packs.filter(p =>
              slot.titles ? (p.pack_type === 'titles') : ((p.pack_type || 'retail') !== 'titles')
            );
            return (
              <div key={slot.key}>
                <label className="block text-[11px] font-medium text-indigo-700 mb-0.5">{slot.label}:</label>
                <select
                  value={routes[slot.key] || ''}
                  onChange={e => handleChange(slot.key, e.target.value)}
                  className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white"
                >
                  <option value="">— not set (old fallback) —</option>
                  {slotPacks.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name} ({p.template_count} forms)
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-indigo-500 mt-2">
        Retail deals pick In-State vs Out-of-State from the deal's out-of-state flag / buyer address. Titles pre-selects its slot's pack — still changeable on the print screen.
      </p>
    </div>
  );
};

export default PackRoutingPanel;
